import {CreateObject, DataObject, Keywords, PostObject, PostObjectResponse, Study} from "./metadata";
import {ExternalProfile, TEST_EXTERNAL_PROFILE} from "./external-profile.model";
import { StrategicPillar } from "../services/strategic-pillar.service";
import { VeevaTaggedAudience } from "../services/audience.service";

export const ACTIVITY_EVENT_OPTIONS = [
  "Advisory Board",
  "External Medical Education",
  "Internal Training",
  "Other"
]

export class Activity extends CreateObject{
  // Update to match API response format
  strategicPillars: StrategicPillar[] = [];
  // Audiences tagged to the activity (from Veeva)
  audiences: VeevaTaggedAudience[] = [];
  
  readonly acID!: string | undefined
  event: string = ""
  title: string = ""
  link: string = ""
  dueDate!: Date
  keyWords: Keywords = []
  externalParticipantIDs: Array<Exclude<typeof ExternalProfile.prototype.eID, undefined>> = []
  externalLeadIDs: Array<Exclude<typeof ExternalProfile.prototype.eID, undefined>> = []
  studies: Array<Study> = []
  notes: string = ""
  isVisible: boolean = true

  static createWithPreselectedDate(preselectedDate: Date){
    const a = new Activity()
    a.dueDate = preselectedDate
    return a
  }

  get abstractDate(): string {
    // Somehow the Date.toISOString subtracts one day, turning 2024 Jan 1 into 2023 Dec 31
    const d = new Date(this.dueDate)
    d.setDate(d.getDate() + 1)
    return d.toISOString().split("T")[0]
  }

  get abstractID(): string {
    return this.acID!
  }

  toPost(changes: Activity): ActivityPost {
    return new ActivityPost(this, changes);
  }

  get studyPresentations(){
    return this.studies.map(s => s.studyNumber + (s.studyName? ", " + s.studyName: ""))
  }

  static override parseFromPostResponse(post: ActivityPostResponse): ActivityPostResponse{
    if(post.updatedData?.dueDate)
      post.updatedData.dueDate = new Date(post.updatedData.dueDate)
    if (post.updatedData) {
      if ((post.updatedData as any).strategicPillars) {
        (post.updatedData as any).strategicPillars = (post.updatedData as any).strategicPillars.map((p: any) => Object.assign({}, p));
      }
      if ((post.updatedData as any).audiences) {
        (post.updatedData as any).audiences = (post.updatedData as any).audiences.map((a: any) => Object.assign({}, a));
      }
    }
    post.updatedData = Object.assign(new Activity(), post.updatedData);
    return post
  }
  
  // Helper getter to maintain backward compatibility with existing code
  get strategicPillar(): StrategicPillar | undefined {
    return this.strategicPillars && this.strategicPillars.length > 0 ? this.strategicPillars[0] : undefined;
  }
}

export interface ActivityEndpointResponse {
  customActivities: Array<Activity>
}

export const TEST_ACTIVITY = Object.assign(new Activity(), {
  acID: "Activity ID",
  event: "Event type",
  title: "Title",
  link: "https://linkX.com",
  dueDate: new Date('2024-07-16'),
  keyWords: ['Keyword 1', 'Keyword 2', 'Keyword 3'],
  externalParticipantIDs: [TEST_EXTERNAL_PROFILE.eID],
  externalLeadIDs: [TEST_EXTERNAL_PROFILE.eID],
  studies: [{
    sID: 0,
    studyNumber: "Study Number",
    studyName: "Study Name"
  }],
  notes: "This can be a loooooooooooooooooooooooong text",
  isVisible: true,
  audiences: [],
}) as Activity

export class ActivityPost extends PostObject{
  acID: typeof Activity.prototype.acID
  event: typeof Activity.prototype.event
  title: typeof Activity.prototype.title
  link: typeof Activity.prototype.link
  dueDate!: typeof Activity.prototype.abstractDate
  externalParticipantIDs: typeof Activity.prototype.externalParticipantIDs
  externalLeadIDs: typeof Activity.prototype.externalLeadIDs
  studies: string[]
  notes: typeof Activity.prototype.notes
  isVisible: typeof Activity.prototype.isVisible
  strategicPillars?: number[]
  audiences?: number[]

  constructor(source: Activity, newValue: Activity) {
    super(source, newValue);

    if(source.acID !== newValue.acID){
      throw Error("ActivityPost: acID cannot be overwritten");
    }

    this.acID = source.acID
    this.event = newValue.event
    this.title = newValue.title
    this.link = newValue.link
    this.dueDate = newValue.abstractDate
    this.externalParticipantIDs = this.flagDifferencesInArray(source.externalParticipantIDs, newValue.externalParticipantIDs)
    this.externalLeadIDs = this.flagDifferencesInArray(source.externalLeadIDs, newValue.externalLeadIDs)
    this.studies = this.flagDifferencesInArray(source.studies.map(s => "" + s.sID), newValue.studies.map(s => "" + s.sID))
    this.notes = newValue.notes
    this.isVisible = newValue.isVisible
  }

  override getSourceWrapper(): { (post: ActivityPostResponse): ActivityPostResponse } {
    return Activity.parseFromPostResponse
  }
}

export type ActivityPostResponse = PostObjectResponse<Activity>
